import { RefObject, useLayoutEffect } from 'react';

/**
 * Checks if the menu element is visually clipped by any ancestor with
 * overflow: hidden/auto/scroll.
 */
function isClippedByAncestor(menu: HTMLElement): boolean {
  const menuRect = menu.getBoundingClientRect();
  let parent = menu.parentElement;

  while (parent && parent !== document.documentElement) {
    const style = window.getComputedStyle(parent);
    const { overflow, overflowX, overflowY } = style;
    const combined = `${overflow} ${overflowX} ${overflowY}`;

    if (combined.includes('hidden') || combined.includes('auto') || combined.includes('scroll')) {
      const parentRect = parent.getBoundingClientRect();
      if (
        menuRect.bottom > parentRect.bottom ||
        menuRect.top < parentRect.top ||
        menuRect.left < parentRect.left ||
        menuRect.right > parentRect.right
      ) {
        return true;
      }
    }
    parent = parent.parentElement;
  }

  return false;
}

/**
 * Hook that adjusts dropdown menu position to keep it fully visible.
 * Uses useLayoutEffect to measure and adjust before the browser paints.
 *
 * Handles two cases:
 * 1. Viewport overflow: flips vertical/horizontal alignment via inline styles
 * 2. Ancestor overflow clipping: switches to position:fixed to escape
 *    overflow:hidden/auto/scroll parents
 *
 * The menu element should render with default Tailwind classes for positioning
 * (e.g. `top-full mt-1 right-0`). This hook overrides via inline styles only
 * when the default position would cause clipping.
 */
export default function useDropdownPosition(
  menuRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  preferHorizontal: 'left' | 'right' = 'right',
): void {
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!isOpen || !menu) return;

    const trigger = menu.offsetParent as HTMLElement | null;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const gap = 4;
    const padding = 8;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    // Determine optimal vertical direction
    const spaceBelow = viewportH - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openAbove =
      menuRect.height > spaceBelow - padding && spaceAbove > spaceBelow;

    // Determine optimal horizontal alignment
    let alignLeft = preferHorizontal === 'left';
    if (!alignLeft) {
      const leftEdge = triggerRect.right - menuRect.width;
      if (leftEdge < padding) alignLeft = true;
    } else {
      const rightEdge = triggerRect.left + menuRect.width;
      if (rightEdge > viewportW - padding) alignLeft = false;
    }

    // Check if any ancestor clips the menu
    const clipped = isClippedByAncestor(menu);

    if (clipped) {
      // Escape overflow clipping by switching to fixed positioning
      menu.style.position = 'fixed';
      menu.style.margin = '0';

      if (openAbove) {
        menu.style.top = 'auto';
        menu.style.bottom = `${viewportH - triggerRect.top + gap}px`;
      } else {
        menu.style.top = `${triggerRect.bottom + gap}px`;
        menu.style.bottom = 'auto';
      }

      if (alignLeft) {
        menu.style.left = `${triggerRect.left}px`;
        menu.style.right = 'auto';
      } else {
        menu.style.left = 'auto';
        menu.style.right = `${viewportW - triggerRect.right}px`;
      }
    } else {
      // Adjust absolute positioning via inline style overrides
      if (openAbove) {
        menu.style.top = 'auto';
        menu.style.bottom = '100%';
        menu.style.marginTop = '0';
        menu.style.marginBottom = '0.25rem';
      }

      if (alignLeft && preferHorizontal === 'right') {
        menu.style.right = 'auto';
        menu.style.left = '0';
      } else if (!alignLeft && preferHorizontal === 'left') {
        menu.style.left = 'auto';
        menu.style.right = '0';
      }
    }
  }, [isOpen, menuRef, preferHorizontal]);
}
